import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { initFlowbite } from 'flowbite';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { SwPartNumberState } from '../../../../../shared/enums/sw-part-number-state';
import { ErrorMessage } from '../../../../../shared/models/error-message/error-message';
import { Policy } from '../../../../../shared/models/info/policy';
import { SelectionOptionValue } from '../../../../../shared/models/internal/selection-option-value';
import { errorToString } from '../../../../common/common-utils';
import { AccessRightsService } from '../../../../services/access-rights.service';
import { ErrorMessageApiService } from '../../../../services/api/error-message-api.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { SwPartNumberService } from '../../../../services/sw-part-number.service';
import { EditTechnicalRedactionReviewableBase } from '../../../base/edit-technical-redaction-reviewable/edit-technical-redaction-reviewable-base';
import { LinkedDtcsComponent } from '../../../common/details/linked-dtcs/linked-dtcs.component';
import { SelectionComponent } from '../../../common/selection/selection.component';
import { TextAreaComponent } from '../../../common/text-area/text-area.component';
import { FloppyIconComponent } from '../../../icons/floppy-icon/floppy-icon.component';
import { LabelWithHelp } from '../../../common/label-with-help/label-with-help';
import { PropagateErrorMessageChangesToEntitiesInOtherVersionsModal } from '../../../modals/propagate-changes-to-entities-in-other-versions-modal/propagate-error-message-changes-to-entities-in-other-versions-modal';
import { EntityPropertyUpdatePropagationInfo } from '../../../../../shared/models/entity-property-info/internal/entity-property-update-propagation-info';
import { ErrorMessageUpdate } from '../../../../../shared/models/error-message/error-message-update';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-edit-error-message',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    TextAreaComponent,
    SelectionComponent,
    LinkedDtcsComponent,
    FloppyIconComponent,
    LabelWithHelp,
    PropagateErrorMessageChangesToEntitiesInOtherVersionsModal,
    AgentActionDirective,
  ],
  templateUrl: './edit-error-message.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-error-message.component.scss',
})
export class EditErrorMessageComponent
  extends EditTechnicalRedactionReviewableBase<ErrorMessage>
  implements OnInit, OnChanges, AfterViewInit
{
  @Output() public errorMessageUpdated = new EventEmitter<ErrorMessage>();

  @ViewChild('propagateChangesToEntitiesInOtherVersions')
  public propagateChangesToEntitiesInOtherVersionsModal?: PropagateErrorMessageChangesToEntitiesInOtherVersionsModal;

  public errorCategories: SelectionOptionValue[] = [];
  public previousErrorMessage?: ErrorMessage;
  public updateTranslationFieldDisabled = true;
  public changeCategoryDisabled = false;
  public sendButtonDisabled = false;

  protected override formGroup: FormGroup<{
    headerOnTerminal: FormControl<string | null>;
    detailsOnTerminal: FormControl<string | null>;
    category: FormControl<string | null>;
    internalNotes: FormControl<string | null>;
  }>;

  private errorMessageApi = inject(ErrorMessageApiService);
  private toast = inject(HotToastService);
  private swPartNumberService = inject(SwPartNumberService);
  private accessRightsService = inject(AccessRightsService);
  private appInsightsService = inject(AppInsightsService);

  constructor() {
    super(STORAGE_KEYS.UNSAVED_ERROR_MESSAGE_CHANGES);
    this.formGroup = this.formBuilder.group({
      headerOnTerminal: new FormControl<string | null>(null, []),
      detailsOnTerminal: new FormControl<string | null>(null, []),
      category: new FormControl<string | null>(null, []),
      internalNotes: new FormControl<string | null>(null, []),
    });
  }

  public ngOnInit(): void {
    this.initializeErrorMessageDetails();
    this.appInsightsService.logSidebarUsage('EM_edit');
  }

  public ngOnChanges(): void {
    this.initializeErrorMessageDetails();
    this.resetFieldTracking$.next(undefined);
    this.previousItemId = this.item?.id;
  }

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    initFlowbite();
  }

  public requestPropagationOfChangesToEntitiesInOtherVersions(): void {
    this.item = this.getCurrentItemFromFormValues();
    this.propagateChangesToEntitiesInOtherVersionsModal?.openModal(this.item);
  }

  public get isNotDefaultLanguage(): boolean {
    return !this.translationService.defaultLanguageSelected();
  }

  protected confirmChanges(propagationInfos?: EntityPropertyUpdatePropagationInfo[]): void {
    if (this.item?.id) {
      const errorMessageUpdate: ErrorMessageUpdate = {
        errorMessage: this.getCurrentItemFromFormValues(),
        entityPropertyUpdateSynchronisationInfos: propagationInfos ?? [],
      };

      this.errorMessageApi.updateErrorMessage(errorMessageUpdate).subscribe({
        next: (updatedErrorMessage) => {
          this.toast.success('Error message updated successfully');
          this.errorMessageUpdated.emit(this.item);
          this.item!.rowVersion = updatedErrorMessage.rowVersion;
          this.resetFieldTracking$.next(undefined);
        },
        error: (error) => {
          console.error(errorToString(error));
        },
      });
    }
  }

  protected override getCurrentItemFromFormValues(): ErrorMessage {
    const formValues = this.formGroup.value;
    const currentErrorMessage: ErrorMessage = {
      ...this.item!,
      headerOnTerminal: formValues.headerOnTerminal || undefined,
      detailsOnTerminal: formValues.detailsOnTerminal || undefined,
      category: formValues.category || undefined,
      internalNotes: formValues.internalNotes === '' ? undefined : formValues.internalNotes || undefined,
    };
    return currentErrorMessage;
  }

  private initializeErrorMessageDetails(): void {
    this.accessRightsService.getAccessRights$().subscribe((ar) => {
      this.changeCategoryDisabled = !ar.authorized(Policy.canUpdateBasic);
      this.updateTranslationFieldDisabled =
        !ar.authorized(Policy.canUpdateBasic) ||
        (!this.translationService.defaultLanguageSelected() && !ar.authorized(Policy.canUpdateTranslations));
    });
    this.fetchErrorCategories();
    this.configureErrorMessageFormState();
  }

  private fetchErrorCategories(): void {
    this.errorMessageApi.getErrorCategories().subscribe({
      next: (categories) => {
        this.errorCategories = categories.map((category) => {
          return { id: category, selected: false, title: category };
        });
      },
      error: (error) => {
        console.error('Error fetching error categories:', errorToString(error));
      },
    });
  }

  private configureErrorMessageFormState(): void {
    if (this.item) {
      this.formGroup.patchValue(this.item);
    }

    const swPartNumberState = this.swPartNumberService.getSwPartNumber()?.state ?? SwPartNumberState.Snapshot;
    if (swPartNumberState == SwPartNumberState.Snapshot) {
      this.formGroup.disable();
      this.sendButtonDisabled = true;
    } else {
      this.formGroup.enable();
      this.sendButtonDisabled = false;

      if (swPartNumberState == SwPartNumberState.Released) {
        this.formGroup.get('headerOnTerminal')?.disable();
        this.formGroup.get('detailsOnTerminal')?.disable();
        this.formGroup.get('category')?.disable();
      } else {
        this.formGroup.get('headerOnTerminal')?.enable();
        this.formGroup.get('detailsOnTerminal')?.enable();
        this.formGroup.get('category')?.enable();
      }
    }

    if (this.changeCategoryDisabled) {
      this.formGroup.get('category')?.disable();
    }
  }
}
